import {
  SIZE_FILTER_ID,
  COLOR_FILTER_ID,
  PRICE_FILTER_ID,
  DEFAULT_SORT,
  SORT_NAME_MAP,
} from '../consts';
import { mapFilters } from './filter-mapper';
import { getFrom, getTo } from '@/utils/price-input-fix';

function fixPriceFilterInput({responseFilter, existsAppliedFilter}) {
  const {min, max} = responseFilter.values[0];
  const resMin = parseInt(min);
  const resMax = parseInt(max);
  const resFrom = parseInt(existsAppliedFilter.value[0].from);
  const resTo = parseInt(existsAppliedFilter.value[0].to);

  return {
    from: getFrom({from: resFrom, to: resTo, min: resMin}),
    to: getTo({from: resFrom, to: resTo, max: resMax}),
  }
}

export default {
  namespaced: true,

  state: () => ({
    categoryName: '',
    categoryId: '',
    productsCount: 0,
    products: [],
    filters: [],
    appliedFiltersState: [],
    sort: DEFAULT_SORT,
    loadProgress: false,
    totalPages: 0,
    page: 0, // номер следующей
    seo: {},
  }),

  getters: {
    products: state => state.products,
    filters: state => state.filters,
    productsCount: state => state.productsCount,
    loadProgress: state => state.loadProgress,
    end: state => state.page >= state.totalPages,
    categoryName: state => state.categoryName,
    sizeFilter: state => state.filters.find(filter => filter.id === SIZE_FILTER_ID),
    colorFilter: state => state.filters.find(filter => filter.id === COLOR_FILTER_ID),
    priceFilter: state => state.filters.find(filter => filter.id === PRICE_FILTER_ID),
    sizeFilterValue: state => state.appliedFiltersState.find(filter => filter.id === SIZE_FILTER_ID)?.value,
    colorFilterValue: state => state.appliedFiltersState.find(filter => filter.id === COLOR_FILTER_ID)?.value,
    priceFilterValue: state => state.appliedFiltersState.find(filter => filter.id === PRICE_FILTER_ID)?.value,
    appliedFiltersCount: state => state.appliedFiltersState.length,
    getFilterValue: state => filterId => state.appliedFiltersState.find(filter => filter.id === filterId)?.value,
    getFilter: state => filterId => state.filters.find(filter => filter.id === filterId),
    categoryUrl: (state, getters, rootState) => rootState.route.path || '',
    categoryId: state => state.categoryId,
    filterURLParam: (state) => {
      const params = state.appliedFiltersState
        .map(filter => {
          if (Array.isArray(filter.value) && filter.value?.[0]?.value) {
            return [filter.id, filter.value.map(i => i.value)]
          } else {
            return [filter.id, Object.values(...filter.value)]
          }
        });
      params.push(['sort', state.sort]);

      return new URLSearchParams(params);
    },
    seo: state => state.seo || {},
  },

  mutations: {
    appendProducts: (state, content = []) => {
      content.forEach(product => state.products.push(product));
    },

    setProducts: (state, content = []) => {
      state.products = content;
    },

    setFilters: (state, filters  = []) => {
      state.filters = filters.filter(f => {
        return !["availability", "delivery", "pickup"].includes(f.id);
      });
    },

    setProductsCount: (state, totalElements = 0) => {
      state.productsCount = totalElements;
    },

    setCategoryName: (state, name = '') => {
      state.categoryName = name;
    },

    setCategoryId: (state, id = '') => {
      state.categoryId = id;
    },

    setLoadProgress: (state, loadProgress = true) => {
      state.loadProgress = loadProgress;
    },

    setTotalPages: (state, totalPages = 0) => {
      state.totalPages = totalPages;
    },

    setPage: (state, page = state.page + 1) => {
      state.page = page;
    },

    setAppliedFiltersState(state, filterData) {
      filterData.forEach(filter => {
        let existsFilter = state.appliedFiltersState
          .find(existsFilter => existsFilter.id === filter.id);

        if (existsFilter) {
          existsFilter.value = filter.value
        } else {
          state.appliedFiltersState.push(filter)
        }
      });
    },

    clearFilter(state, filterId) {
      const filterIndex = state.appliedFiltersState
        .findIndex(filter => filter.id === filterId);

      if(filterIndex >= 0) {
        state.appliedFiltersState.splice(filterIndex, 1);
      }
    },

    clearAllFilters(state) {
      state.appliedFiltersState = [];
    },

    setSort(state, sort) {
      const sortVal = SORT_NAME_MAP[sort] ? sort : DEFAULT_SORT;
      state.sort = sortVal;
    },

    setSeo(state, seo = {}) {
      state.seo = seo;
    }
  },

  actions: {
    loadData({ state, dispatch, commit, rootState }) {
      if (state.loadProgress) return;

      commit('setLoadProgress');
      if (rootState.route.name === 'Search') {
        return dispatch('loadSearchPage');
      }

      return dispatch('loadCategoryPage');
    },

    loadSearchPage({ state, commit, rootState }, query = rootState.route.query.query ) {
      return new Promise((resolve, reject) => {
        this.$api.searchProducts({
          query,
          pageNumber: state.page,
        })
          .then(({ content, filter, totalElements, totalPages }) => {
            if ( // 302 редирект на поиск по артикулу
              totalElements === 1
              && content?.[0]?.models?.some(m => m.code.includes(query))
            ) {
              reject({ status: 302, redirect: `/product/${content[0].id}` });
            }
            commit('appendProducts', content);
            commit('setFilters', filter);
            commit('setProductsCount', totalElements);
            commit('setTotalPages', totalPages);
            commit('setPage');
            resolve();
          })
          .catch(reject)
          .finally(() => {
            commit('setLoadProgress', false);
          });
      });
    },

    setLoadedCategoryData({ commit }, {name, id, filter, totalElements, totalPages, seo} = {}) {
      commit('setCategoryName', name);
      commit('setCategoryId', id);
      commit('setFilters', filter);
      commit('setProductsCount', totalElements);
      commit('setTotalPages', totalPages);
      commit('setPage');
      commit('setLoadProgress', false);
      commit('setSeo', seo);
    },

    sendRequest({ state }, categoryUrl) {
      const encodedCategoryUrl = encodeURIComponent(categoryUrl);

      return this.$api.loadCategory({
        categoryUrl: encodedCategoryUrl,
        filters: mapFilters(state.appliedFiltersState),
        sort: state.sort,
        pageNumber: state.page,
      });
    },

    async loadCategoryPage({ dispatch, commit, rootState }, {
      categoryUrl = rootState.route.path,
    } = {} ) {
      const result = await dispatch('sendRequest', categoryUrl);
      await dispatch('setLoadedCategoryData', result);

      if (result?.content) {
        commit('appendProducts', result.content);
      }
    },

    applyFiltersState({ commit }, filterData) {
      filterData.forEach(({id, value}) => {
        if (!value.length) {
          commit('clearFilter', id);
        } else {
          commit('setAppliedFiltersState', [{ id, value }]);
        }
      });
    },

    clearFiltersStateFromNotExistsValues({ dispatch, commit, state }, responseFilters) {
      const stateAndResponseFilters = state.appliedFiltersState.map(filter => {
        const existsResponseFilter = responseFilters.find(responseFilter => responseFilter.id === filter.id);

        return [filter, existsResponseFilter];
      });

      stateAndResponseFilters.forEach(([existsAppliedFilter, responseFilter]) => {
        // если нет такого фильтра - удаляем фильтр
        if(!responseFilter) {
          commit('clearFilter', existsAppliedFilter.id);

          return;
        }

        // отдельная обработка для цены
        if (existsAppliedFilter.id === 'price') {
          const { from, to } = fixPriceFilterInput({ responseFilter, existsAppliedFilter });

          dispatch('applyFiltersState', [{ id: existsAppliedFilter.id, value: [{from, to}] }]);

          return;
        }

        // если нет такого значения фильтра - удаляем значение
        const responseFilterValues = responseFilter.values.map(v => v.value);
        const existsValues = existsAppliedFilter.value.filter(appliedValue => {
          return responseFilterValues.includes(appliedValue.value)
        });

        dispatch('applyFiltersState', [{ id: existsAppliedFilter.id, value: existsValues }]);
      });
    },

    async applyCategoryFilter({ dispatch, commit, rootState }, { filterData, sort } = {}) {
      commit('setLoadProgress');
      commit('setPage', 0);

      if (filterData) {
        dispatch('applyFiltersState', filterData);
      }

      if (sort) {
        commit('setSort', sort);
      }

      const result = await dispatch('sendRequest', rootState.route.path);
      // удаляем не существующие/не валидные фильтры и сортировки
      await dispatch('clearFiltersStateFromNotExistsValues', result.filter);
      await dispatch('setLoadedCategoryData', result);

      commit('setProducts', result.content);
    },

    clearAppliedFilter({ dispatch, commit }, filterId) {
      commit('clearFilter', filterId);
      dispatch('applyCategoryFilter');
    },

    clearAllAppliedFilters({ dispatch, commit }) {
      commit('clearAllFilters');
      dispatch('applyCategoryFilter');
    },

    clearAllFiltersAndSort({ dispatch, commit }) {
      commit('clearAllFilters');
      commit('setSort', DEFAULT_SORT);
      dispatch('applyCategoryFilter');
    },
  }
};
